import { Form } from "antd";
import { useEffect, useState } from "react";
import {
  addAssetType,
  deleteAssetType,
  getCompanyIndustries,
  updateAssetType,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { AssetType } from "../repository/data.default";
import { AssetTypesForm } from "./AssetTypesFormDialog.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { hasFormTouched } from "../utils/hasFormChanged";
import { getProblemMessage } from "../utils/http";

export interface AssetTypesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: AssetType;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// Industry/domain options (optional — lets the company-asset type picker filter by the company's industry).
const industryRequest = async () => {
  const { data } = await getCompanyIndustries({
    searchQuery: { conditions: [] },
    pageNumber: 0,
    pageSize: 200,
  });
  return data.map((d) => ({ display: d.name, value: d.uid }));
};

function AssetTypesFormDialog(props: AssetTypesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<AssetTypesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      if (
        props.initialValues &&
        (props.mode === "update" || props.mode === "delete")
      ) {
        form.setFieldsValue({
          name: props.initialValues.name,
          description: props.initialValues.description,
          domain: props.initialValues.domainUid
            ? {
                display: props.initialValues.domainName,
                value: props.initialValues.domainUid,
              }
            : undefined,
        });
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, description, domain } = values;
    const domainUid = (domain as any)?.value;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addAssetType({ name, description, domainUid })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateAssetType({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
          domainUid,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteAssetType(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Asset Type", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [{ required: true, message: "Name is required" }],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                  },
                  {
                    type: "combobox",
                    name: "domain",
                    label: "Industry (optional)",
                    request: industryRequest,
                    tooltip:
                      "Tie this asset type to an industry so the company-asset form can suggest matching types.",
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="asset type"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default AssetTypesFormDialog;
